import { ChevronUp, ChevronDown } from 'lucide-react';
import { TableHead } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { SortOrder } from '@/hooks/useAnalytics';

interface SortableHeaderProps<TField extends string> {
  label: string;
  field: TField;
  currentField: TField;
  currentOrder: SortOrder;
  onSort: (field: TField) => void;
  className?: string;
  width?: string;
  /** Shown on hover over the column label. */
  tooltip?: string;
}

export const SortableHeader = <TField extends string>({
  label,
  field,
  currentField,
  currentOrder,
  onSort,
  className,
  width,
  tooltip,
}: SortableHeaderProps<TField>) => {
  const isRightAligned = className?.includes('text-right');
  const isActive = currentField === field;
  const sortButton = (
    <button
      type="button"
      className="cursor-pointer rounded-sm transition-colors hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary"
      onClick={() => onSort(field)}
    >
      {label}
    </button>
  );

  return (
    <TableHead
      className={cn('select-none', className)}
      style={width ? { width } : undefined}
      aria-sort={isActive ? (currentOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className={cn('flex items-center gap-1', isRightAligned && 'justify-end')}>
        {tooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{sortButton}</TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          sortButton
        )}
        {isActive && (
          currentOrder === 'asc' ? (
            <ChevronUp className="h-3 w-3 text-slate-500" />
          ) : (
            <ChevronDown className="h-3 w-3 text-slate-500" />
          )
        )}
      </div>
    </TableHead>
  );
};
